import EntityType from '@enums/EntityType';
import {
  PropertyEntity,
  PropertyValueEntity,
  SkillEntity,
  SkillLevelEntity,
  TagEntity,
  TagCategoryEntity,
} from '@enums/MappedEntityTypes';
import resolveActivityProcess from '@utils/entityMaps/resolveActivityProcess';
import isProcess from '@utils/processes/isProcess';
import untaggedTag from '@utils/tags/untaggedTag';
import { PropertyType } from '@/lib/enums';
import addTreeItemMetrics from './addTreeItemMetrics';
import buildParentTreeItem from './buildParentTreeItem';
import buildSubTreeItems from './buildSubTreeItems';
import filterEntity, { filterSameAsParent } from './filterEntity';
import getTreeItemType from './getTreeItemType';
import resolveEntityTagIds from './resolveEntityTagIds';

const buildConnectedSubTreeItem = ({
  designEntities,
  connectedParentItem,
  entity,
  entities,
  includeEmptyValues,
  includeMetrics,
  library,
  metrics,
  order,
  orderIndex,
  parentItem,
  peopleSkillMap,
  tree,
}) => {
  const currentOrder = order[orderIndex];
  const nextOrderItem = order[orderIndex + 1];
  const nextEntity = entities[nextOrderItem?.type];

  const entitySet = currentOrder.entitySet;

  const item = tree[entity.parentId]
    ? tree[entity.parentId]
    : { id: entity.parentId, type: entity.type, parentItem };

  item.totalMetrics = addTreeItemMetrics({
    designEntities,
    entity,
    entities,
    entitySet,
    property: 'totalMetrics',
    treeItem: item,
  });

  const parentEntity =
    orderIndex < order.length - 1
      ? buildParentTreeItem({ entity, entities, orderItem: order[orderIndex] })
      : null;

  if (!item.children) {
    item.children = {};
  }

  if (!item.children[entity.childId]) {
    item.children[entity.childId] = {
      id: entity.childId,
      type: entity.childType,
      parentId: entity.parentId,
      parentItem: connectedParentItem,
    };
    item.childType = entity.childType;
  }

  const childItem = item.children[entity.childId];

  childItem.totalMetrics = addTreeItemMetrics({
    designEntities,
    entity,
    entities,
    entitySet,
    property: 'totalMetrics',
    treeItem: childItem,
  });

  let hasCompleteValidLeaf = false;

  if (!nextEntity) {
    childItem.children = null;
    if (includeMetrics) {
      childItem.metrics = addTreeItemMetrics({
        designEntities,
        entity,
        entities,
        entitySet,
        id: entity.childId,
        treeItem: childItem,
      });
      hasCompleteValidLeaf = true; // reached end + included → valid leaf
    }

    if (hasCompleteValidLeaf || (!hasCompleteValidLeaf && includeEmptyValues)) {
      tree[item.id] = item;
    }
  } else {
    const childTree = childItem.children || {};
    const result = buildTreePath({
      designEntities,
      entity: nextEntity,
      entities,
      includeMetrics,
      library,
      order,
      orderIndex: orderIndex + 1,
      parentItem: parentEntity,
      peopleSkillMap,
      tree: childTree,
    });

    childItem.childType = getTreeItemType(
      nextOrderItem.type,
      nextOrderItem.subType
    );

    if (result.tree && Object.keys(result.tree).length > 0) {
      childItem.children = result.tree;
    } else if (!childItem.children) {
      childItem.children = null;
    }

    // Only add metrics if the subtree below reached a complete valid leaf
    if (includeMetrics && result.hasCompleteValidLeaf) {
      childItem.metrics = addTreeItemMetrics({
        designEntities,
        entity,
        entities,
        entitySet,
        id: entity.childId,
        treeItem: childItem,
      });
    }

    hasCompleteValidLeaf = result.hasCompleteValidLeaf;

    item.children[childItem.id] = childItem;
    tree[item.id] = item;
  }

  return { tree, hasCompleteValidLeaf };
};

const buildTreeItem = ({
  designEntities,
  entity,
  entities,
  includeEmptyValues,
  includeMetrics,
  library,
  order,
  orderIndex,
  parentItem,
  peopleSkillMap,
  tree,
}) => {
  const entitySet = order[orderIndex].entitySet;

  const item = tree[entity.uuid]
    ? tree[entity.uuid]
    : {
        id: entity.uuid,
        type: entity.__type,
        parentId: entity.parentId,
        parentItem,
      };

  item.totalMetrics = addTreeItemMetrics({
    designEntities,
    entity,
    entities,
    entitySet,
    property: 'totalMetrics',
    treeItem: item,
  });

  const parentEntity =
    orderIndex < order.length - 1
      ? buildParentTreeItem({ entity, entities, orderItem: order[orderIndex] })
      : null;

  if (!item.parentItem && parentItem) {
    item.parentItem = parentItem;
  }

  const nextOrderItem = order[orderIndex + 1];
  const nextEntity = entities[nextOrderItem?.type];

  let hasCompleteValidLeaf = false;

  if (!nextEntity) {
    if (item.children) {
      return { tree, hasCompleteValidLeaf };
    }
    item.children = null;
    if (nextOrderItem) {
      item.childType = getTreeItemType(
        nextOrderItem.type,
        nextOrderItem.subType
      );
    }
    if (includeMetrics) {
      item.metrics = addTreeItemMetrics({
        designEntities,
        entity,
        entities,
        entitySet,
        id: entity.uuid,
        treeItem: item,
      });
      hasCompleteValidLeaf = true; // reached end + included
    }
    if (!hasCompleteValidLeaf && !includeEmptyValues) {
      return { tree, hasCompleteValidLeaf };
    }

    tree[item.id] = item;
    return { tree, hasCompleteValidLeaf };
  }

  item.childType = getTreeItemType(nextOrderItem.type, nextOrderItem.subType);

  const childTree = item?.children || {};
  const result = buildTreePath({
    designEntities,
    entity: nextEntity,
    entities,
    includeMetrics,
    library,
    order,
    orderIndex: orderIndex + 1,
    parentItem: parentEntity,
    peopleSkillMap,
    tree: childTree,
  });

  if (result.tree && Object.keys(result.tree).length > 0) {
    item.children = result.tree;
  } else if (!item.children) {
    item.children = null;
  }

  if (includeMetrics && result.hasCompleteValidLeaf) {
    item.metrics = addTreeItemMetrics({
      designEntities,
      entity,
      entities,
      entitySet,
      id: entity.uuid,
      treeItem: item,
    });
    hasCompleteValidLeaf = true;
  }

  if (!hasCompleteValidLeaf && !includeEmptyValues) {
    return { tree, hasCompleteValidLeaf };
  }

  tree[item.id] = item;

  return { tree, hasCompleteValidLeaf };
};

const buildProcessTreePath = ({
  designEntities,
  entities,
  includeEmptyValues,
  includeMetrics,
  library,
  order,
  orderIndex,
  parentItem,
  peopleSkillMap,
  tree,
}) => {
  const activity = entities[EntityType.ACTIVITY];
  if (!activity || !activity.visible) {
    return { tree, hasCompleteValidLeaf: false };
  }

  const currentOrder = order[orderIndex];
  const allowedIds = currentOrder.allowedIds;
  const entitySet = currentOrder.entitySet;

  const { l1, l2 } = resolveActivityProcess(activity, library);

  // No L2 means the old expanded PROCESS level was null — no valid leaf.
  if (!l1 || !l2) {
    return { tree, hasCompleteValidLeaf: false };
  }

  // User selects L2 process ids on the Processes source.
  if (allowedIds && !allowedIds.has(l2.uuid)) {
    return { tree, hasCompleteValidLeaf: false };
  }

  const processOrder =
    order[orderIndex + 1]?.type === EntityType.PROCESS
      ? [...order.slice(0, orderIndex + 1), ...order.slice(orderIndex + 2)]
      : order;

  const connectedParentItem = {
    type: EntityType.L1_PROCESS,
    typeId: l1.uuid,
  };

  const result = buildConnectedSubTreeItem({
    designEntities,
    connectedParentItem,
    entity: {
      parentId: l1.uuid,
      childId: l2.uuid,
      type: EntityType.L1_PROCESS,
      childType: EntityType.PROCESS,
    },
    entities,
    includeEmptyValues,
    includeMetrics,
    library,
    order,
    orderIndex,
    parentItem,
    peopleSkillMap,
    tree,
  });

  const updatedTree = result.tree;

  if (includeMetrics && result.hasCompleteValidLeaf && updatedTree?.[l1.uuid]) {
    updatedTree[l1.uuid].metrics = addTreeItemMetrics({
      designEntities,
      entity: activity,
      entities,
      entitySet,
      id: l1.uuid,
      treeItem: updatedTree[l1.uuid],
    });
  }

  return {
    tree: updatedTree,
    hasCompleteValidLeaf: result.hasCompleteValidLeaf,
  };
};

const buildPropertyTreePath = ({
  designEntities,
  entity,
  entities,
  includeMetrics,
  library,
  order,
  orderIndex,
  parentItem,
  peopleSkillMap,
  tree,
}) => {
  const currentOrder = order[orderIndex];
  const allowedIds = currentOrder.allowedIds;
  const entitySet = currentOrder.entitySet;
  const propertyEntity = entities[currentOrder.type] || entity;

  let anyCompleteValid = false;

  entity.properties.forEach(({ key, value }) => {
    const property = library.properties.mapped.get(key);
    if (!property || property.type !== PropertyType.DROPDOWN) {
      return;
    }

    // Prevent child item being included in it's parent for complex nesting.
    if (value === parentItem?.valueId) {
      return;
    }

    if (allowedIds && !allowedIds.has(value)) {
      return;
    }

    if (!property.__options.has(value)) {
      return;
    }

    const connectedParentItem = {
      type: entity.__type,
      typeId: entity.uuid,
      subType: EntityType.PROPERTY,
      subTypeId: key,
    };

    let result;

    if (orderIndex > 0) {
      const propertyValueEntity = {
        uuid: value,
        __type: PropertyValueEntity[entity.__type],
        parentId: key,
        __metrics: entity.__metrics,
      };

      result = buildTreeItem({
        designEntities,
        entity: propertyValueEntity,
        entities,
        includeMetrics,
        library,
        order,
        orderIndex,
        parentItem,
        peopleSkillMap,
        tree,
      });
      anyCompleteValid = includeMetrics && result.hasCompleteValidLeaf;
    } else {
      result = buildConnectedSubTreeItem({
        designEntities,
        connectedParentItem,
        entity: {
          parentId: key,
          childId: value,
          type: PropertyEntity[entity.__type],
          childType: PropertyValueEntity[entity.__type],
        },
        entities,
        includeMetrics,
        library,
        order,
        orderIndex,
        parentItem,
        peopleSkillMap,
        tree,
      });
    }

    const updatedTree = result.tree;

    if (includeMetrics && result.hasCompleteValidLeaf && updatedTree?.[key]) {
      updatedTree[key].metrics = addTreeItemMetrics({
        designEntities,
        entity,
        entities,
        entitySet,
        id: key,
        treeItem: updatedTree[key],
      });
      anyCompleteValid = true;
    }

    tree = updatedTree;
  });

  return { tree, hasCompleteValidLeaf: anyCompleteValid };
};

const buildSkillTreePath = ({
  designEntities,
  entity,
  entities,
  includeMetrics,
  library,
  order,
  orderIndex,
  parentItem,
  peopleSkillMap,
  tree,
}) => {
  let skills = entity.skills;
  const currentOrder = order[orderIndex];
  const allowedValues = currentOrder.allowedValues;
  const entitySet = currentOrder.entitySet;
  const isPerson = entity.__type === EntityType.PERSON;

  if (isPerson) {
    const personSkills = peopleSkillMap.get(entity.uuid);

    if (!personSkills?.size) {
      return { tree, hasCompleteValidLeaf: false };
    }

    skills = [...personSkills.entries()].map(([uuid, level]) => ({
      uuid,
      level,
    }));
  }

  if (allowedValues) {
    skills = skills.filter((skill) => {
      const hasSkill = allowedValues?.get(skill.uuid)?.has(skill.level);
      return hasSkill;
    });
  }

  if (!skills.length) {
    return { tree, hasCompleteValidLeaf: false };
  }

  let anyCompleteValid = false;

  skills.forEach(({ uuid, level }) => {
    // Prevent the skill being added if the parent has the exact same id and
    // level.
    if (
      parentItem &&
      parentItem?.type === currentOrder.type &&
      parentItem.subTypeId === uuid &&
      parentItem.valueId === level
    ) {
      return;
    }

    if (!library.skills.map.has(uuid)) {
      return;
    }
    const connectedParentItem = {
      type: entity.__type,
      typeId: entity.uuid,
      subType: EntityType.SKILL,
      subTypeId: uuid,
    };

    let result;

    if (orderIndex > 0) {
      const skillLevelEntity = {
        uuid,
        __type: SkillEntity[entity.__type],
        parentId: level,
      };

      result = buildTreeItem({
        designEntities,
        entity: skillLevelEntity,
        entities,
        includeMetrics,
        library,
        order,
        orderIndex,
        parentItem,
        peopleSkillMap,
        tree,
      });
      anyCompleteValid = includeMetrics && result.hasCompleteValidLeaf;
    } else {
      result = buildConnectedSubTreeItem({
        designEntities,
        connectedParentItem,
        entity: {
          parentId: uuid,
          childId: level,
          type: SkillEntity[entity.__type],
          childType: SkillLevelEntity[entity.__type],
        },
        entities,
        includeMetrics,
        library,
        order,
        orderIndex,
        parentItem,
        tree,
      });
    }

    const updatedTree = result.tree;

    if (includeMetrics && result.hasCompleteValidLeaf && updatedTree?.[uuid]) {
      updatedTree[uuid].metrics = addTreeItemMetrics({
        designEntities,
        entity,
        entities,
        entitySet,
        id: uuid,
        treeItem: updatedTree[uuid],
      });
      anyCompleteValid = true;
    }

    tree = updatedTree;
  });

  return { tree, hasCompleteValidLeaf: anyCompleteValid };
};

const buildTagCategoryTreePath = ({
  designEntities,
  entity,
  entities,
  includeMetrics,
  library,
  order,
  orderIndex,
  parentItem,
  peopleSkillMap,
  tree,
}) => {
  const currentOrder = order[orderIndex];
  const allowedIds = currentOrder.allowedIds;
  const entitySet = currentOrder.entitySet;

  let anyCompleteValid = false;

  const tags = resolveEntityTagIds(entity, library);

  tags.forEach((id) => {
    const tag =
      id === EntityType.UNTAGGED
        ? untaggedTag()
        : library.tags.tags.map.get(id);

    const categoryId =
      tag.__source === 'SYSTEM' ? 'system' : tag.category_uuid || 'primary';

    if (allowedIds && !allowedIds.has(id)) {
      return;
    }

    const connectedParentItem = {
      type: entity.__type,
      typeId: entity.uuid,
      subType: EntityType.TAG_CATEGORY,
      subTypeId: categoryId,
    };

    const result = buildConnectedSubTreeItem({
      designEntities,
      connectedParentItem,
      entity: {
        parentId: categoryId,
        childId: id,
        type: TagCategoryEntity[entity.__type],
        childType: TagEntity[entity.__type],
      },
      entities,
      includeMetrics,
      library,
      order,
      orderIndex,
      peopleSkillMap,
      parentItem,
      tree,
    });

    const updatedTree = result.tree;

    if (
      includeMetrics &&
      result.hasCompleteValidLeaf &&
      updatedTree?.[categoryId]
    ) {
      updatedTree[categoryId].metrics = addTreeItemMetrics({
        designEntities,
        entity,
        entities,
        entitySet,
        id: categoryId,
        treeItem: updatedTree[categoryId],
      });
      anyCompleteValid = true;
    }

    tree = updatedTree;
  });

  return { tree, hasCompleteValidLeaf: anyCompleteValid };
};

export default function buildTreePath({
  designEntities,
  entities,
  includeEmptyValues,
  includeMetrics,
  library,
  order,
  orderIndex = 0,
  parentItem,
  peopleSkillMap,
  tree = {},
}) {
  const currentOrderItem = order[orderIndex];

  // Ensure the activity is the entity type used for processes.
  const entity = isProcess(currentOrderItem?.type)
    ? entities[EntityType.ACTIVITY]
    : entities[order[orderIndex]?.type];

  if (!entity || (entity.__type !== EntityType.PERSON && !entity.visible)) {
    return { tree, hasCompleteValidLeaf: false };
  }

  if (filterSameAsParent(entity, currentOrderItem, parentItem)) {
    return { tree, hasCompleteValidLeaf: true };
  }

  if (filterEntity(entity, currentOrderItem, parentItem)) {
    return { tree, hasCompleteValidLeaf: false };
  }

  let result;

  if (isProcess(currentOrderItem?.type)) {
    return buildProcessTreePath({
      designEntities,
      entities,
      includeEmptyValues,
      includeMetrics,
      library,
      order,
      orderIndex,
      parentItem,
      peopleSkillMap,
      tree,
    });
  }

  if (currentOrderItem.subType) {
    if (currentOrderItem.subType === EntityType.PROCESS) {
      console.log('Hi');
    }
    if (currentOrderItem.subType === EntityType.PROPERTY) {
      result = buildPropertyTreePath({
        designEntities,
        entities,
        entity,
        includeEmptyValues,
        includeMetrics,
        library,
        order,
        orderIndex,
        parentItem,
        peopleSkillMap,
        tree,
      });
    } else if (currentOrderItem.subType === EntityType.SKILL) {
      result = buildSkillTreePath({
        designEntities,
        entities,
        entity,
        includeEmptyValues,
        includeMetrics,
        library,
        order,
        orderIndex,
        parentItem,
        peopleSkillMap,
        tree,
      });
    } else if (
      orderIndex === 0 &&
      order[0].subType === EntityType.TAG &&
      currentOrderItem.subType === EntityType.TAG
    ) {
      result = buildTagCategoryTreePath({
        designEntities,
        entities,
        entity,
        includeEmptyValues,
        includeMetrics,
        library,
        order,
        orderIndex,
        parentItem,
        peopleSkillMap,
        tree,
      });
    } else {
      const { subItems, type } = buildSubTreeItems({
        entity,
        orderItem: currentOrderItem,
      });

      if (!subItems.length) {
        return { tree, hasCompleteValidLeaf: false };
      }

      let anyComplete = false;

      subItems?.forEach((item) => {
        const id = item?.uuid || item?.key || item;

        if (id === parentItem?.subTypeId) {
          anyComplete = true;
          return;
        }

        const childResult = buildTreeItem({
          designEntities,
          entity: { uuid: id, __type: type },
          entities,
          includeEmptyValues,
          includeMetrics,
          library,
          order,
          orderIndex,
          parentItem,
          peopleSkillMap,
          tree,
        });

        if (childResult.hasCompleteValidLeaf) {
          anyComplete = true;
        }
      });

      return { tree, hasCompleteValidLeaf: anyComplete };
    }
  } else {
    result = buildTreeItem({
      designEntities,
      entity,
      entities,
      includeEmptyValues,
      includeMetrics,
      library,
      order,
      orderIndex,
      parentItem,
      peopleSkillMap,
      tree,
    });
  }

  return result;
}
